<template>
  <!-- ICONIC: Benutzer-Liste mit Rollen in einfachen Worten -->
  <v-container class="users">
    <div class="d-flex flex-wrap align-end mb-4">
      <div class="mr-auto">
        <h1 class="text-h5 font-weight-medium">Benutzer & Rechte</h1>
        <div class="text-body-2 text--secondary">
          Person antippen, um ihre Rolle und Rechte zu ändern.
        </div>
      </div>
    </div>

    <v-card outlined>
      <v-card-title class="pb-0">
        <v-text-field
          v-model="search"
          prepend-inner-icon="mdi-magnify"
          label="Name suchen"
          clearable
          hide-details
          outlined
          dense
          class="search mr-4 mb-3"
        ></v-text-field>
        <v-chip-group v-model="filter" mandatory class="mb-3">
          <v-chip filter outlined value="all">Alle {{ counts.all }}</v-chip>
          <v-chip filter outlined value="online"
            >Online {{ counts.online }}</v-chip
          >
          <v-chip filter outlined value="admin"
            >Server Admins {{ counts.admin }}</v-chip
          >
          <v-chip filter outlined value="channel"
            >Mit Kanal-Rechten {{ counts.channel }}</v-chip
          >
        </v-chip-group>
      </v-card-title>

      <!-- Handy: einfache Liste statt Tabelle -->
      <template v-if="$vuetify.breakpoint.xsOnly">
        <v-progress-linear v-if="loading" indeterminate></v-progress-linear>
        <p
          v-else-if="!visible.length"
          class="text-body-2 text--secondary px-4 py-3 mb-0"
        >
          Niemand gefunden.
        </p>
        <v-list v-else class="py-0">
          <template v-for="(item, i) in visible">
            <v-divider v-if="i" :key="`d${item.cldbid}`"></v-divider>
            <v-list-item :key="item.cldbid" @click="open(item)">
              <v-list-item-avatar>
                <v-badge
                  dot
                  bordered
                  overlap
                  :color="item.online ? 'success' : 'grey'"
                >
                  <v-avatar size="36" color="secondary" class="white--text">
                    {{ initial(item.name) }}
                  </v-avatar>
                </v-badge>
              </v-list-item-avatar>
              <v-list-item-content>
                <v-list-item-title class="font-weight-medium">
                  {{ item.name }}
                  <v-icon v-if="item.locked" small color="warning"
                    >mdi-lock</v-icon
                  >
                </v-list-item-title>
                <v-list-item-subtitle>
                  {{ item.roleLabel
                  }}{{ item.channelRoles.length ? ` · ${channelSummary(item)}` : "" }}
                </v-list-item-subtitle>
                <v-list-item-subtitle class="text-caption">
                  {{ item.online ? `online in ${item.channelName}` : `zuletzt ${item.lastSeen}` }}
                </v-list-item-subtitle>
              </v-list-item-content>
              <v-list-item-action>
                <v-icon>mdi-chevron-right</v-icon>
              </v-list-item-action>
            </v-list-item>
          </template>
        </v-list>
      </template>

      <v-data-table
        v-else
        :headers="headers"
        :items="filtered"
        :search="search"
        :custom-filter="nameFilter"
        :loading="loading"
        loading-text="Lade Benutzer …"
        no-data-text="Keine Benutzer gefunden."
        no-results-text="Niemand mit diesem Namen."
        :footer-props="{
          'items-per-page-options': [25, 50, 100, -1],
          'items-per-page-text': 'Pro Seite',
          'items-per-page-all-text': 'Alle',
        }"
        :items-per-page="50"
        sort-by="sortKey"
        item-key="cldbid"
        class="row-pointer"
        @click:row="open"
      >
        <template #item.name="{ item }">
          <div class="d-flex align-center py-2">
            <v-badge
              dot
              bordered
              overlap
              :color="item.online ? 'success' : 'grey'"
              class="mr-3"
            >
              <v-avatar size="32" color="secondary" class="white--text">
                {{ initial(item.name) }}
              </v-avatar>
            </v-badge>
            <div>
              <div class="font-weight-medium">
                {{ item.name }}
                <v-icon
                  v-if="item.locked"
                  small
                  color="warning"
                  title="Geschützt: in dieser Oberfläche gesperrt"
                  >mdi-lock</v-icon
                >
              </div>
              <div class="text-caption text--secondary">
                {{ item.online ? `online in ${item.channelName}` : `zuletzt ${item.lastSeen}` }}
              </div>
            </div>
          </div>
        </template>
        <template #item.role="{ item }">
          <v-chip
            small
            :color="item.roleKey === 'admin' ? 'primary' : undefined"
            :outlined="item.roleKey !== 'admin'"
            >{{ item.roleLabel }}</v-chip
          >
          <span
            v-for="extra in item.extraGroups"
            :key="extra"
            class="text-caption text--secondary ml-1"
            >+ {{ extra }}</span
          >
        </template>
        <template #item.channels="{ item }">
          <span v-if="!item.channelRoles.length" class="text--disabled"
            >–</span
          >
          <span v-else class="text-body-2">{{ channelSummary(item) }}</span>
        </template>
        <template #item.open>
          <v-icon>mdi-chevron-right</v-icon>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import {
  loadConfig,
  ensureServer,
  asList,
  resolveGroups,
  serverRoleOf,
  channelRoleLabel,
  channelPaths,
  relativeDate,
  errorText,
} from "@/iconic/rechte";

export default {
  data() {
    return {
      loading: true,
      search: "",
      filter: "all",
      users: [],
      headers: [
        { text: "Name", value: "name", sortable: true },
        { text: "Rolle auf dem Server", value: "role", sortable: false },
        { text: "Rechte in Kanälen", value: "channels", sortable: false },
        { text: "", value: "open", sortable: false, align: "end", width: 40 },
      ],
    };
  },
  computed: {
    filtered() {
      if (this.filter === "online") return this.users.filter((u) => u.online);
      if (this.filter === "admin")
        return this.users.filter((u) => u.roleKey === "admin");
      if (this.filter === "channel")
        return this.users.filter((u) => u.channelRoles.length);
      return this.users;
    },
    visible() {
      const q = (this.search || "").toLowerCase();
      return this.filtered
        .filter((u) => !q || u.sortKey.includes(q))
        .slice()
        .sort((a, b) => a.sortKey.localeCompare(b.sortKey));
    },
    counts() {
      return {
        all: this.users.length,
        online: this.users.filter((u) => u.online).length,
        admin: this.users.filter((u) => u.roleKey === "admin").length,
        channel: this.users.filter((u) => u.channelRoles.length).length,
      };
    },
  },
  methods: {
    initial(name) {
      return (String(name || "?").trim()[0] || "?").toUpperCase();
    },
    nameFilter(value, search, item) {
      if (!search) return true;
      return String(item.name).toLowerCase().includes(search.toLowerCase());
    },
    channelSummary(user) {
      const byRole = {};
      user.channelRoles.forEach((r) => {
        byRole[r.label] = byRole[r.label] || [];
        byRole[r.label].push(r.channel);
      });
      return Object.entries(byRole)
        .map(([label, chans]) =>
          chans.length === 1
            ? `${label} in ${chans[0]}`
            : `${label} in ${chans.length} Kanälen`
        )
        .join(", ");
    },
    open(item) {
      this.$router.push({ name: "user", params: { cldbid: item.cldbid } });
    },
    async load() {
      this.loading = true;
      try {
        await ensureServer();
        const [config, serverInfo, serverGroups, channelGroups, channels] =
          await Promise.all([
            loadConfig(),
            this.$TeamSpeak.execute("serverinfo").then((r) => asList(r)[0]),
            this.$TeamSpeak.execute("servergrouplist").then(asList),
            this.$TeamSpeak.execute("channelgrouplist").then(asList),
            this.$TeamSpeak.execute("channellist").then(asList),
          ]);
        const groups = resolveGroups(serverInfo, serverGroups, channelGroups);
        const paths = {};
        channelPaths(channels).forEach((c) => (paths[c.cid] = c.name));

        const dbList = await this.$TeamSpeak.fullClientDBList();
        const online = asList(await this.$TeamSpeak.execute("clientlist")).filter(
          (c) => String(c.clientType) === "0"
        );

        // Mitglieder je Server-Gruppe (ohne die Standardgruppe, die TeamSpeak nicht einzeln führt)
        const memberOf = {};
        for (const g of serverGroups.filter(
          (g) => String(g.type) === "1" && String(g.sgid) !== groups.defaultSg
        )) {
          const members = asList(
            await this.$TeamSpeak.execute("servergroupclientlist", {
              sgid: g.sgid,
            })
          );
          members.forEach((m) => {
            const id = String(m.cldbid);
            (memberOf[id] = memberOf[id] || []).push(String(g.sgid));
          });
        }

        const channelAssignments = asList(
          await this.$TeamSpeak.execute("channelgroupclientlist")
        ).filter((a) => String(a.cgid) !== groups.defaultCg);

        const standardSg = groups.serverRoles.map((r) => r.sgid);
        this.users = dbList.map((c) => {
          const id = String(c.cldbid);
          const sgids = memberOf[id] || [];
          const role = serverRoleOf(groups, sgids) || { key: "guest", label: "Gast" };
          const here = online.find((o) => String(o.clientDatabaseId) === id);
          const channelRoles = channelAssignments
            .filter((a) => String(a.cldbid) === id && paths[String(a.cid)])
            .map((a) => ({
              label: channelRoleLabel(groups, a.cgid),
              channel: paths[String(a.cid)],
            }));
          return {
            cldbid: id,
            name: c.clientNickname,
            sortKey: String(c.clientNickname || "").toLowerCase(),
            online: Boolean(here),
            channelName: here ? paths[String(here.cid)] || "" : "",
            lastSeen: relativeDate(c.clientLastconnected),
            roleKey: role.key,
            roleLabel: role.label,
            extraGroups: sgids
              .filter((s) => !standardSg.includes(s))
              .map((s) => groups.sgName(s))
              .filter(Boolean),
            channelRoles,
            locked: config.protectedDbIds.includes(id),
          };
        });
      } catch (err) {
        this.$toast.error(errorText(err));
      }
      this.loading = false;
    },
  },
  created() {
    this.load();
  },
};
</script>

<style scoped>
.search {
  max-width: 320px;
}
.row-pointer >>> tbody tr {
  cursor: pointer;
}
</style>
